import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  styled,
} from '@mui/material';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as vehicleSlice from '../../../data/vehicle.slice';
import * as tripSlice from '../../../data/trip.slice';
import ContentSearch from '../ContentSearch';
import {
  DataGridPro,
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortItem,
} from '@mui/x-data-grid-pro';
import { SortModel } from '../../../data/models/sort-model.model';

const PREFIX = 'AssignVehicleDialog';

export const classes = {
  content: `${PREFIX}-content`,
  grid: `${PREFIX}-grid`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface AssignVehicleDialogProps {
  open: boolean;
  providerId: number;
  tripLegReferenceID: string;
  close: (saved: boolean) => void;
}

export default function AssignVehicleDialog({
  open,
  providerId,
  tripLegReferenceID,
  close,
}: AssignVehicleDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const assigning = useAppSelector(tripSlice.selectAssigningVehicleToTrip);
  const assigned = useAppSelector(tripSlice.selectAssignedVehicleToTrip);

  const loading = useAppSelector(vehicleSlice.selectLoadingVehicleList);
  const vehicleList = useAppSelector(vehicleSlice.selectVehicleList);

  const [searchFilter, setSearchFilter] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [sortModel, setSortModel] = React.useState<SortModel | null>(null);
  const [selectedVehicleID, setSelectedVehicleID] = React.useState<
    number | undefined
  >();

  React.useEffect(() => {
    if (assigned) {
      dispatch(tripSlice.setAssignedVehicleToTrip(false));
      closeDialog(true);
    }
  }, [assigned, dispatch]);

  React.useEffect(() => {
    if (!open || !providerId || !tripLegReferenceID) {
      return;
    }

    dispatch(
      vehicleSlice.fetchVehicleListAsync(
        pageSize,
        page,
        '',
        searchFilter,
        sortModel,
        providerId,
      ),
    );
  }, [
    open,
    providerId,
    tripLegReferenceID,
    searchFilter,
    page,
    pageSize,
    sortModel,
    dispatch,
  ]);

  const columns: GridColDef[] = [
    {
      field: 'licensePlate',
      headerName: t('vehicle.license') || '',
      flex: 1,
      sortable: false,
    },
    {
      field: 'vehicleMake',
      headerName: t('vehicle.make') || '',
      flex: 1,
      sortable: false,
    },
    {
      field: 'vehicleModel',
      headerName: t('vehicle.model') || '',
      flex: 1,
      sortable: false,
    },
  ];

  const onSearchChange = (val: string) => {
    setSearchFilter(val);
  };

  const onSortModelChange = (sortItems: GridSortItem[]) => {
    const newModel = sortItems.length
      ? ({
          column: sortItems[0].field,
          direction: sortItems[0].sort,
        } as SortModel)
      : null;
    setSortModel(newModel);
  };

  const onPageChange = (pg: number) => {
    setPage(pg);
  };

  const onPageSizeChange = (pgSize: number) => {
    setPageSize(pgSize);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== vehicleList.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  const onSelectVehicle = (model: GridRowSelectionModel) => {
    setSelectedVehicleID(model.length ? (model[0] as number) : undefined);
  };

  const assignVehicle = () => {
    if (providerId && selectedVehicleID && tripLegReferenceID) {
      dispatch(
        tripSlice.assignVehicleToTripAsync(
          selectedVehicleID,
          tripLegReferenceID,
        ),
      );
    }
  };

  const closeDialog = (saved: boolean) => {
    setSearchFilter('');
    setPage(1);
    setSortModel(null);
    setSelectedVehicleID(undefined);
    close(saved);
  };

  return (
    <StyledDialog open={open} fullWidth={true} maxWidth="lg">
      <DialogTitle>{t('trips.assignVehicle')}</DialogTitle>
      <DialogContent className={classes.content}>
        <ContentSearch
          icon={<AirportShuttleIcon />}
          placeholder={t('vehicles.search')}
          currentSearchFilter={searchFilter}
          onSearchChange={onSearchChange}
        />
        <DataGridPro
          getRowId={(row: any) => row.vehicleID}
          rows={vehicleList.items}
          columns={columns}
          pagination={true}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: vehicleList.currentPage - 1, pageSize }}
          rowCount={vehicleList.itemCount}
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          className={classes.grid}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          sortModel={
            sortModel
              ? [
                  {
                    field: sortModel.column,
                    sort: sortModel.direction,
                  },
                ]
              : []
          }
          onPaginationModelChange={paginationChange}
          onSortModelChange={onSortModelChange}
          onRowSelectionModelChange={onSelectVehicle}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={assignVehicle}
          disabled={!selectedVehicleID || assigning}>
          {t('trips.assign')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => closeDialog(false)}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
